import React from 'react';
import { AxiosResponse, AxiosError } from 'axios';
import { Button, createStyles, Grid, Icon, makeStyles, Theme, Typography } from '@material-ui/core';

import { KeywordInclusionContext } from '../KeywordInclusionContext';
import { KeywordInclusionSearchResult } from '../components/KeywordInclusionSearchResult';
import { KeywordInclusionFilterFields } from '../components/KeywordInclusionFilterFields';
import { KeywordInclusionAddNewKeywordDialog } from '../components/KeywordInclusionAddNewKeywordDialog';
import { CategoryKeywordSearchModel } from '../../../Models/CategoryKeywordSearchModel';
import { ClientModel, clientModelInitial } from '../../../Models/ClientModel';
import { KeywordModel, keywordModelInitial } from '../../../Models/KeywordModel';
import ClientService from '../../../Services/ClientService';
import KeywordService from '../../../Services/KeywordService';
import StorageKeywordSearchService from '../../../Services/StorageKeywordSearchService';
import { BasicPage } from '../../../shared/components/BasicPage';
import { SearchStatus } from '../../../shared/enum/SearchStatus';
import { AuthContext } from '../../Authentication/AuthContext';
import { HomeContext } from '../../Home/HomeContext';
import { SearchButtons } from '../../../shared/components/SearchButtons';
import { KeywordListForClient } from '../../../shared/components/KeywordListForClient';
import { FilterChipsSearchField } from '../../../shared/components/FilterChipsSearchField';

const useStyles = makeStyles((theme: Theme) => createStyles({
    sectionDescription: {
        marginBottom: theme.spacing(3)
    },
    titleSection: {
        marginBottom: theme.spacing(2)
    },
    searchButtonsGrid: {
        marginTop: theme.spacing(3)
    },
    addNewButton: {
        paddingRight: 0
    }
}));


export function KeywordInclusion() {
    const classes = useStyles();

    const authContext = React.useContext(AuthContext);
    const homeContext = React.useContext(HomeContext);

    const [selectedClient, setSelectedClient] = React.useState<ClientModel>(clientModelInitial);
    const [selectedKeyword, setSelectedKeyword] = React.useState<KeywordModel>(keywordModelInitial);
    const [selectedInitialDate, setSelectedInitialDate] = React.useState<Date | null>(null);
    const [selectedFinalDate, setSelectedFinalDate] = React.useState<Date | null>(null);
    const [clientList, setClientList] = React.useState<ClientModel[]>([]);
    const [keywordList, setKeywordList] = React.useState<KeywordModel[]>([]);
    const [searchKeywordText, setSearchKeywordText] = React.useState<string>('');

    const [categoryKeywordSearchResult, setCategoryKeywordSearchResult] = React.useState<CategoryKeywordSearchModel[]>([]);
    const [searchStatus, setSearchStatus] = React.useState<SearchStatus>(SearchStatus.INITIAL);

    const [openAddNewKeywordDialog, setOpenAddNewKeywordDialog] = React.useState<boolean>(false);
    const [clientError, setClientError] = React.useState<boolean>(false);
    const [keywordError, setKeywordError] = React.useState<boolean>(false);
    const [initialDateError, setInitialDateError] = React.useState<boolean>(false);
    const [finalDateError, setFinalDateError] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (clientList.length == 0) {

            homeContext.setOpenLoadingDialog(true);
            ClientService.clientByUserService(authContext.login.user.id)
                .getAll()
                .then((res: AxiosResponse) => {
                    setClientList(res.data);

                    if (res.data.length === 1)
                        setSelectedClient(res.data[0]);
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar os clientes. <br/>', error))
                .finally(() => homeContext.setOpenLoadingDialog(false));
        }
    }, []);

    React.useEffect(() => {
        setSelectedKeyword(keywordModelInitial);

        if (selectedClient.id > 0) {
            homeContext.setOpenLoadingDialog(true);
            KeywordService.keywordService(selectedClient.id)
                .getAll()
                .then((res: AxiosResponse) => {
                    const noneElements = res.data.filter((k: KeywordModel) => k.description.toLowerCase() === 'nenhum');
                    const otherElements = res.data.filter((k: KeywordModel) => k.description.toLowerCase() !== 'nenhum');
                    const sortedKeywords = otherElements.sort((a: KeywordModel, b: KeywordModel) =>
                        (a.description).localeCompare(b.description)); 

                    setKeywordList([...noneElements, ...sortedKeywords]);
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar as palavras chaves do cliente. <br/>', error))
                .finally(() => homeContext.setOpenLoadingDialog(false));
        }
        else
            setKeywordList([]);
    }, [selectedClient]);
    
    const handleCleanFilters = () => {
        setSelectedClient(clientModelInitial);
        setSelectedKeyword(keywordModelInitial);
        setCategoryKeywordSearchResult([]);
        setSelectedInitialDate(null);
        setSelectedFinalDate(null);
        setClientError(false);
        setKeywordError(false);
        setInitialDateError(false);
        setFinalDateError(false);
        setSearchStatus(SearchStatus.INITIAL);
    };

    const handleSearch = () => {
        if (!hasError()) {
            setSearchStatus(SearchStatus.LOADING);
            StorageKeywordSearchService
                .getNewsByKeyword(
                    selectedClient.id,
                    selectedKeyword.description,
                    selectedInitialDate ?? new Date(Date.now()),
                    selectedFinalDate ?? new Date(Date.now()))
                .then((res: AxiosResponse) => {
                    setCategoryKeywordSearchResult(res.data);
                    if (res.data.length > 1)
                        setSearchStatus(SearchStatus.RESULT);
                    else
                        setSearchStatus(SearchStatus.EMPTY); })
                .catch((error: AxiosError) => {
                    homeContext.showError('', error);
                    setSearchStatus(SearchStatus.EMPTY); });
        }
    };

    const hasError = () => {
        const clientNotSelectedError = !selectedClient || selectedClient.id <= 0;
        const initialDateNotSelectedError = !selectedInitialDate;
        const finalDateNotSelectedError = !selectedFinalDate;
        const keywordNotSelectedError = !selectedKeyword || !selectedKeyword.description ;
        
        setClientError(clientNotSelectedError);
        setInitialDateError(initialDateNotSelectedError);
        setFinalDateError(finalDateNotSelectedError);
        setKeywordError(keywordNotSelectedError);

        return clientNotSelectedError || initialDateNotSelectedError || finalDateNotSelectedError || keywordNotSelectedError;
    };

    const getFilterSection = () => {
        return (
            <Grid container direction="row" spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="body1" className={classes.sectionDescription}>
                        Selecione o cliente para realizar a pesquisa e a inclusão de palavras-chave.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <KeywordInclusionFilterFields />
                </Grid>
            </Grid>
        );
    };

    const getKeywordSearchFieldAndButton = () => {
        return (
            <Grid
                container
                direction="row"
                spacing={3}
                justifyContent="space-between">
                <Grid item xs={12} sm={6}>
                    <FilterChipsSearchField
                        key="keyword-inclusion-search-field"
                        searchText={ searchKeywordText }
                        setSearchText={ setSearchKeywordText }
                        error={ keywordError && (!selectedKeyword || !selectedKeyword.description) }
                        errorText="Selecione uma palavra-chave" />
                </Grid>
                { keywordList.length > 0 && (
                    <Grid item xs={4} sm={4} style={{ textAlign: 'right'}}>
                        <Button
                            size='large'
                            onClick={() => setOpenAddNewKeywordDialog(true)}
                            startIcon={<Icon color='primary'>add</Icon>}
                            className={ classes.addNewButton }>
                            ADICIONAR NOVA PALAVRA CHAVE
                        </Button>
                    </Grid>
                )}
            </Grid>
        );
    };

    const getKeywordsSection = () => {
        return (
            <Grid container direction="row" spacing={1}>
                <Grid item xs={12} sm={12}>
                    <Typography variant="body1" className={ classes.sectionDescription }>
                        Selecione as palavras chaves para incluir nas matérias.
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <Typography variant="subtitle1" className={ classes.titleSection }>
                        Palavras-chave
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    { getKeywordSearchFieldAndButton() }
                </Grid>
                <Grid item xs={12} sm={12}>
                    <KeywordListForClient
                        key="client-keywords"
                        selectedKeywords={ selectedKeyword }
                        keywordList={ keywordList }
                        onSelectKeyword={ (keyword: KeywordModel) => setSelectedKeyword(keyword) }
                        onDeselectKeyword={ (_) => setSelectedKeyword(keywordModelInitial) }
                        searchTextFilter={ searchKeywordText } />
                </Grid>
            </Grid>
        );
    };

    return (
        <KeywordInclusionContext.Provider value={{
            selectedClient, setSelectedClient,
            clientList, setClientList,
            selectedInitialDate, setSelectedInitialDate,
            selectedFinalDate, setSelectedFinalDate,
            selectedKeyword, setSelectedKeyword,
            keywordList, setKeywordList,
            clientError, setClientError,
            initialDateError, setInitialDateError,
            finalDateError, setFinalDateError,
            searchStatus, setSearchStatus,
            categoryKeywordSearchResult, setCategoryKeywordSearchResult,
            openAddNewKeywordDialog, setOpenAddNewKeywordDialog
        }}>
            <BasicPage
                title="Incluir palavra-chave"
                subtitle="">
                <>
                    <Grid item xs={12}>
                        { getFilterSection() }
                    </Grid>

                    <Grid item xs={12}>
                        { getKeywordsSection() }
                    </Grid>

                    <SearchButtons
                        searchStatus={searchStatus}
                        handleCleanFilter={handleCleanFilters}
                        handleSearch={handleSearch}/>

                    <Grid item xs={12}>
                        <KeywordInclusionSearchResult />
                    </Grid>
                    
                    <Grid item>
                        <KeywordInclusionAddNewKeywordDialog />
                    </Grid>
                </> 
            </BasicPage>
        </KeywordInclusionContext.Provider>
    );
}